/**
 * Novidades do painel (I5), lidas de um arquivo VERSIONADO (`novidades.json`).
 *
 * O arquivo é validado aqui na carga: data em AAAA-MM-DD, id único e começando pela data,
 * e a mais nova primeiro. Um erro de digitação no JSON quebra o teste
 * (`test/experience-changelog.test.ts`), não a tela do cliente.
 */
import { z } from 'zod';

import raw from './novidades.json';

const entrySchema = z.object({
  id: z.string().regex(/^\d{4}-\d{2}-\d{2}(?:-[a-z0-9]+)+$/),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  title: z.string().min(1).max(120),
  items: z.array(z.string().min(1).max(300)).min(1),
});

const fileSchema = z.object({ entries: z.array(entrySchema).min(1) });

export type ChangelogEntry = z.infer<typeof entrySchema>;

export function parseChangelog(input: unknown): ChangelogEntry[] {
  const { entries } = fileSchema.parse(input);
  const ids = new Set<string>();
  entries.forEach((entry, index) => {
    if (!entry.id.startsWith(entry.date)) {
      throw new Error(`Novidade ${entry.id}: o id precisa começar pela data (${entry.date}).`);
    }
    if (ids.has(entry.id)) throw new Error(`Novidade repetida: ${entry.id}.`);
    ids.add(entry.id);
    const previous = entries[index - 1];
    if (previous && previous.date < entry.date) {
      throw new Error(`Novidades fora de ordem: ${entry.id} veio depois de ${previous.id}.`);
    }
  });
  return entries;
}

export const CHANGELOG: readonly ChangelogEntry[] = parseChangelog(raw);

export const LATEST_CHANGELOG_ID: string = CHANGELOG[0]?.id ?? '';

/**
 * Tem novidade para esta pessoa? `seenId` nulo = nunca abriu (tudo é novo). Um id visto que
 * saiu do arquivo não faz o ponto acender para sempre: compara pela data do id.
 */
export function hasUnreadChangelog(
  seenId: string | null,
  entries: readonly ChangelogEntry[] = CHANGELOG,
): boolean {
  const latest = entries[0];
  if (!latest) return false;
  if (seenId === null) return true;
  if (seenId === latest.id) return false;
  return latest.id > seenId;
}

/** "7 de outubro de 2026" — data de calendário, sem hora (não depende de fuso). */
export function formatChangelogDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  return new Intl.DateTimeFormat('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}
